import { create } from "zustand";
import type { Awareness } from "y-protocols/awareness";

export interface PresenceUser {
  clientId: number;
  name: string;
  color: string;
  cursor: { line: number; ch: number } | null;
  sectionId: string | null;
  lastKeystroke: number;
  typingVelocity: number;
  isActive: boolean;
}

interface PresenceState {
  peers: Map<number, PresenceUser>;
  localCursorLine: number;
  localSectionId: string | null;

  setPeers: (peers: Map<number, PresenceUser>) => void;
  setLocalCursor: (line: number, sectionId: string | null) => void;
  getNearbyPeers: (line: number, radius?: number) => PresenceUser[];
  getSectionActivity: (sectionId: string) => { count: number; avgVelocity: number };
}

const NEARBY_RADIUS = 20;

export const usePresenceStore = create<PresenceState>()((set, get) => ({
  peers: new Map(),
  localCursorLine: 0,
  localSectionId: null,

  setPeers: (peers) => set({ peers }),

  setLocalCursor: (line, sectionId) =>
    set({ localCursorLine: line, localSectionId: sectionId }),

  getNearbyPeers: (line, radius = NEARBY_RADIUS) => {
    const { peers } = get();
    const result: PresenceUser[] = [];
    peers.forEach((peer) => {
      if (peer.cursor && Math.abs(peer.cursor.line - line) <= radius) {
        result.push(peer);
      }
    });
    return result;
  },

  getSectionActivity: (sectionId) => {
    const { peers } = get();
    let count = 0;
    let totalVelocity = 0;
    peers.forEach((peer) => {
      if (peer.sectionId === sectionId && peer.isActive) {
        count++;
        totalVelocity += peer.typingVelocity;
      }
    });
    return {
      count,
      avgVelocity: count > 0 ? totalVelocity / count : 0,
    };
  },
}));

let cleanupFn: (() => void) | null = null;

/**
 * Bind the presence store to a Yjs awareness instance.
 * Call once when collaboration starts; returns a cleanup function.
 */
export function bindPresenceToAwareness(
  awareness: Awareness,
  localName: string,
  localColor: string,
): () => void {
  if (cleanupFn) cleanupFn();

  const keystrokeTimestamps: number[] = [];
  const VELOCITY_WINDOW_MS = 5000;
  const THROTTLE_MS = 100;
  let lastBroadcast = 0;

  function computeVelocity(): number {
    const now = Date.now();
    const cutoff = now - VELOCITY_WINDOW_MS;
    while (keystrokeTimestamps.length > 0 && keystrokeTimestamps[0] < cutoff) {
      keystrokeTimestamps.shift();
    }
    return (keystrokeTimestamps.length / VELOCITY_WINDOW_MS) * 60000;
  }

  function broadcastLocal() {
    const now = Date.now();
    if (now - lastBroadcast < THROTTLE_MS) return;
    lastBroadcast = now;

    const store = usePresenceStore.getState();
    awareness.setLocalStateField("presence", {
      cursor: { line: store.localCursorLine, ch: 0 },
      sectionId: store.localSectionId,
      lastKeystroke: now,
      typingVelocity: computeVelocity(),
    });
  }

  function syncPeers() {
    const states = awareness.getStates();
    const localId = awareness.clientID;
    const peers = new Map<number, PresenceUser>();

    states.forEach((state, clientId) => {
      if (clientId === localId) return;
      const user = state.user as { name?: string; color?: string } | undefined;
      const presence = state.presence as {
        cursor?: { line: number; ch: number };
        sectionId?: string | null;
        lastKeystroke?: number;
        typingVelocity?: number;
      } | undefined;

      if (!user) return;

      const lastKeystroke = presence?.lastKeystroke ?? 0;
      const isActive = Date.now() - lastKeystroke < 10000;

      peers.set(clientId, {
        clientId,
        name: user.name ?? "Anonymous",
        color: user.color ?? "#5B5FFF",
        cursor: presence?.cursor ?? null,
        sectionId: presence?.sectionId ?? null,
        lastKeystroke,
        typingVelocity: presence?.typingVelocity ?? 0,
        isActive,
      });
    });

    usePresenceStore.getState().setPeers(peers);
  }

  awareness.setLocalStateField("user", { name: localName, color: localColor });
  broadcastLocal();

  const handleChange = () => syncPeers();
  awareness.on("change", handleChange);
  syncPeers();

  let prevLine = usePresenceStore.getState().localCursorLine;
  let prevSection = usePresenceStore.getState().localSectionId;
  const cursorUnsub = usePresenceStore.subscribe((state) => {
    if (state.localCursorLine !== prevLine || state.localSectionId !== prevSection) {
      prevLine = state.localCursorLine;
      prevSection = state.localSectionId;
      broadcastLocal();
    }
  });

  const activityInterval = setInterval(() => syncPeers(), 5000);

  cleanupFn = () => {
    awareness.off("change", handleChange);
    cursorUnsub();
    clearInterval(activityInterval);
    cleanupFn = null;
  };

  return cleanupFn;
}

export function recordKeystroke() {
  // No-op placeholder for keystroke tracking; called from editor
}
